import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  MARQUEE_GESTURE_MIN_PX,
  type MarqueeDragState,
  collectRowIdsInMarquee,
  marqueeDragToBox,
} from '../util/workListMarqueeUtils';

type UseScrollContainerMarqueeSelectOptions<T> = {
  items: T[];
  getItemId: (item: T) => string;
  rowSelector?: string;
  getRowIdFromElement?: (el: HTMLElement) => string | undefined;
  containerRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  onCommit: (hitItems: T[]) => void;
};

const DEFAULT_ROW_SELECTOR = '[data-work-id]';
const defaultGetRowIdFromElement = (el: HTMLElement) => el.dataset.workId;

/**
 * 스크롤 컨테이너 안의 data-* 행에 대해 로컬 상태로 마퀴 선택(WorkList 마퀴와 동일 좌표계).
 * 전역 `workListMarqueeStore`를 쓰지 않음(모달 등 격리된 목록용).
 */
export function useScrollContainerMarqueeSelect<T>({
  items,
  getItemId,
  rowSelector = DEFAULT_ROW_SELECTOR,
  getRowIdFromElement = defaultGetRowIdFromElement,
  containerRef,
  enabled,
  onCommit,
}: UseScrollContainerMarqueeSelectOptions<T>): {
  handleContainerMouseDown: (e: ReactMouseEvent<HTMLElement>) => void;
  handleContainerClickCapture: (e: ReactMouseEvent<HTMLElement>) => void;
  marqueeBox: ReturnType<typeof marqueeDragToBox>;
  marqueeSelecting: boolean;
  hitItems: T[];
} {
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  const marqueeExceededThresholdRef = useRef(false);
  const suppressNextRowClickRef = useRef(false);
  const suppressClickResetTimerRef = useRef<number | null>(null);
  const hitItemsRef = useRef<T[]>([]);

  const [marqueeDrag, setMarqueeDrag] = useState<MarqueeDragState | null>(null);
  const [hitItems, setHitItems] = useState<T[]>([]);

  useLayoutEffect(() => {
    if (marqueeDrag === null) {
      if (enabled && hitItemsRef.current.length > 0) {
        onCommitRef.current([...hitItemsRef.current]);
      }
      hitItemsRef.current = [];
      setHitItems((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    if (!enabled) return;

    const el = containerRef.current;
    const box = marqueeDragToBox(marqueeDrag);
    if (!el || !box) {
      hitItemsRef.current = [];
      setHitItems((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const byId = new Map(items.map((x) => [getItemId(x), x] as const));
    const mapped = collectRowIdsInMarquee(el, box, rowSelector, getRowIdFromElement)
      .map((id) => byId.get(id))
      .filter((w): w is T => !!w);

    hitItemsRef.current = mapped;
    setHitItems(mapped);
  }, [enabled, marqueeDrag, items, containerRef, getItemId, rowSelector, getRowIdFromElement]);

  useLayoutEffect(() => {
    if (enabled) return;
    hitItemsRef.current = [];
    setHitItems((prev) => (prev.length === 0 ? prev : []));
    setMarqueeDrag(null);
  }, [enabled]);

  const handleContainerClickCapture = useCallback((e: ReactMouseEvent<HTMLElement>) => {
    if (!suppressNextRowClickRef.current) return;
    if (suppressClickResetTimerRef.current !== null) {
      window.clearTimeout(suppressClickResetTimerRef.current);
      suppressClickResetTimerRef.current = null;
    }
    suppressNextRowClickRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleContainerMouseDown = useCallback(
    (e: ReactMouseEvent<HTMLElement>) => {
      if (!enabled) return;
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest('[data-suppress-marquee]')) return;

      const el = containerRef.current;
      if (!el) return;

      e.preventDefault();
      marqueeExceededThresholdRef.current = false;
      const rect = el.getBoundingClientRect();
      const ox = e.clientX - rect.left + el.scrollLeft;
      const oy = e.clientY - rect.top + el.scrollTop;

      const onMove = (ev: globalThis.MouseEvent) => {
        const r = el.getBoundingClientRect();
        const cx = ev.clientX - r.left + el.scrollLeft;
        const cy = ev.clientY - r.top + el.scrollTop;
        if (
          Math.abs(cx - ox) >= MARQUEE_GESTURE_MIN_PX ||
          Math.abs(cy - oy) >= MARQUEE_GESTURE_MIN_PX
        ) {
          marqueeExceededThresholdRef.current = true;
        }
        setMarqueeDrag((prev) => (prev ? { ...prev, currentX: cx, currentY: cy } : null));
      };

      const onUp = () => {
        if (marqueeExceededThresholdRef.current) {
          suppressNextRowClickRef.current = true;
          suppressClickResetTimerRef.current = window.setTimeout(() => {
            suppressNextRowClickRef.current = false;
            suppressClickResetTimerRef.current = null;
          }, 0);
        }
        marqueeExceededThresholdRef.current = false;
        setMarqueeDrag(null);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      setMarqueeDrag({
        originX: ox,
        originY: oy,
        currentX: ox,
        currentY: oy,
      });
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [enabled, containerRef]
  );

  const marqueeBox = marqueeDragToBox(marqueeDrag);

  return {
    handleContainerMouseDown,
    handleContainerClickCapture,
    marqueeBox,
    marqueeSelecting: !!marqueeDrag,
    hitItems,
  };
}
